import React, { useCallback } from 'react';
import { MetaAdCampaignObjective } from '@frontend/gql/social/graphql';
import {
  CommandSelect,
  CommandSelectTrigger,
  CommandSelectValue,
  CommandSelectContent,
  CommandSelectEmpty,
  CommandSelectLoading,
  CommandSelectItem,
  CommandSelectAll,
  useCommandSelectSearch,
} from '../../Analytics/components/filters-form/command-select';

// Type for Ad Campaign Objectives using the real GraphQL enum
interface AdCampaignObjective {
  id: string;
  name: string;
  objective: MetaAdCampaignObjective;
}

interface AdCampaignObjectivesSelectProps {
  children: React.ReactNode;
}

export const AdCampaignObjectivesSelect: React.FC<AdCampaignObjectivesSelectProps> = ({ children }) => {
  return <div className="relative">{children}</div>;
};

// Content component that can access CommandSelect context
const AdCampaignObjectivesContent: React.FC<{
  adCampaignObjectives: AdCampaignObjective[];
  isLoading: boolean;
  selectedIds: string[];
  isUpdating: boolean;
  handleSelectObjective: (objectiveId: string) => void;
  updateSelection: (selection: string[]) => void;
}> = ({ adCampaignObjectives, isLoading, selectedIds, isUpdating, handleSelectObjective, updateSelection }) => {
  // Get search query for filtering (now inside CommandSelect context)
  const searchQuery = useCommandSelectSearch();

  // Filter objectives based on search query
  const filteredObjectives = React.useMemo(() => {
    if (!searchQuery || searchQuery.trim() === '') {
      return adCampaignObjectives;
    }

    const query = searchQuery.toLowerCase().trim();
    return adCampaignObjectives.filter(
      (objective) => objective.name.toLowerCase().includes(query) || objective.id.toLowerCase().includes(query),
    );
  }, [adCampaignObjectives, searchQuery]);

  // Combine filtered objectives with selected objectives (similar to other components pattern)
  const displayObjectives = React.useMemo(() => {
    const objectiveMap = new Map<string, AdCampaignObjective & { state: 'filtered' | 'selected' }>();

    // Add filtered objectives
    filteredObjectives.forEach((objective) => {
      objectiveMap.set(objective.id, { ...objective, state: 'filtered' });
    });

    // Add selected objectives that might not be in filtered results
    selectedIds.forEach((objectiveId) => {
      if (!objectiveMap.has(objectiveId)) {
        const selectedObjective = adCampaignObjectives.find((obj) => obj.id === objectiveId);
        if (selectedObjective) {
          objectiveMap.set(objectiveId, { ...selectedObjective, state: 'selected' });
        }
      }
    });

    return Array.from(objectiveMap.values());
  }, [filteredObjectives, selectedIds, adCampaignObjectives]);

  // Sort objectives: selected first, then alphabetical
  const sortedObjectives = React.useMemo(() => {
    return [...displayObjectives].sort((a, b) => {
      const aSelected = selectedIds.includes(a.id);
      const bSelected = selectedIds.includes(b.id);

      // Selected items go to the top
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // If both selected or both unselected, maintain alphabetical order
      return a.name.localeCompare(b.name);
    });
  }, [displayObjectives, selectedIds]);

  // Get visible objective IDs for Select All logic
  const visibleObjectiveIds = React.useMemo(() => {
    return filteredObjectives.map((objective) => objective.id);
  }, [filteredObjectives]);

  // Get selected IDs that are currently visible
  const selectedVisibleIds = React.useMemo(() => {
    return selectedIds.filter((id) => visibleObjectiveIds.includes(id));
  }, [selectedIds, visibleObjectiveIds]);

  // Create a wrapped handleSelectAll that has access to filteredObjectives
  const wrappedHandleSelectAll = React.useCallback(() => {
    if (isUpdating || isLoading) {
      return;
    }

    let newSelection: string[];

    if (selectedVisibleIds.length === visibleObjectiveIds.length && visibleObjectiveIds.length > 0) {
      // Deselect all visible objectives (preserve selections from other search contexts)
      newSelection = selectedIds.filter((id) => !visibleObjectiveIds.includes(id));
    } else {
      // Select all visible objectives (preserve existing selections)
      newSelection = Array.from(new Set([...selectedIds, ...visibleObjectiveIds]));
    }

    updateSelection(newSelection);
  }, [visibleObjectiveIds, selectedVisibleIds, selectedIds, updateSelection, isUpdating, isLoading]);

  return (
    <>
      {isLoading ? (
        <CommandSelectLoading>Loading ad objectives...</CommandSelectLoading>
      ) : (
        <>
          {/* Select All/Deselect All */}
          {filteredObjectives.length > 0 && (
            <CommandSelectAll
              checked={selectedVisibleIds.length === visibleObjectiveIds.length && visibleObjectiveIds.length > 0}
              indeterminate={selectedVisibleIds.length > 0 && selectedVisibleIds.length < visibleObjectiveIds.length}
              disabled={isLoading || isUpdating}
              onToggle={wrappedHandleSelectAll}
              count={filteredObjectives.length}
            />
          )}

          {sortedObjectives.length > 0 ? (
            sortedObjectives.map((objective) => (
              <CommandSelectItem
                key={objective.id}
                value={objective.id}
                onSelect={() => handleSelectObjective(objective.id)}
                selected={selectedIds.includes(objective.id)}
                disabled={isLoading || isUpdating}
              >
                <span>{objective.name}</span>
              </CommandSelectItem>
            ))
          ) : (
            <CommandSelectEmpty>No ad objectives found.</CommandSelectEmpty>
          )}
        </>
      )}
    </>
  );
};

export const AdCampaignObjectivesOptions = ({
  adCampaignObjectives = [],
  isLoading = false,
  value,
  onChange,
}: {
  adCampaignObjectives?: AdCampaignObjective[];
  isLoading?: boolean;
  value: string[];
  onChange: (ids: string[]) => void;
}) => {
  // Local state management to prevent infinite loops (same pattern as other select components)
  const [selectedIds, setSelectedIds] = React.useState<string[]>(value || []);
  const [isUpdating, setIsUpdating] = React.useState(false);

  // Unified update handler with proper state management
  const updateSelection = React.useCallback(
    (newSelection: string[]) => {
      setIsUpdating(true);
      setSelectedIds(newSelection);
      onChange(newSelection);
    },
    [onChange],
  );

  // Sync selectedIds with value prop and manage isUpdating flag
  React.useEffect(() => {
    const selectedSet = new Set(selectedIds);
    const valueArr = value || [];
    const valueSet = new Set(valueArr);
    const arraysAreEqual = selectedSet.size === valueSet.size && [...selectedSet].every((id) => valueSet.has(id));

    if (!isUpdating) {
      if (!arraysAreEqual) {
        setSelectedIds(valueArr);
      }
    } else {
      if (arraysAreEqual) {
        setIsUpdating(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isUpdating, selectedIds]);

  const handleSelectObjective = useCallback(
    (objectiveId: string) => {
      if (isUpdating) return;

      const isCurrentlySelected = selectedIds.includes(objectiveId);

      if (isCurrentlySelected) {
        // Remove from selected
        const newSelection = selectedIds.filter((id) => id !== objectiveId);
        updateSelection(newSelection);
      } else {
        // Add to selected
        const newSelection = [...selectedIds, objectiveId];
        updateSelection(newSelection);
      }
    },
    [selectedIds, updateSelection, isUpdating],
  );

  const displayText = React.useMemo(() => {
    if (selectedIds.length === 0) return { mainText: 'Select ad objectives...', count: null };
    if (selectedIds.length === 1) {
      const selectedObjective = adCampaignObjectives.find((objective) => objective.id === selectedIds[0]);
      return { mainText: selectedObjective?.name || 'Ad Objective Selected', count: null };
    }
    const firstObjective = adCampaignObjectives.find((objective) => objective.id === selectedIds[0]);
    return { mainText: firstObjective?.name || 'Ad Objectives Selected', count: selectedIds.length - 1 };
  }, [selectedIds, adCampaignObjectives]);

  const handleDismiss = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      updateSelection([]);
    },
    [updateSelection],
  );

  return (
    <CommandSelect>
      <CommandSelectTrigger placeholder="Select ad objectives...">
        <CommandSelectValue
          placeholder="Select ad objectives..."
          displayText={selectedIds.length > 0 ? displayText.mainText : undefined}
          count={displayText.count}
          onClear={handleDismiss}
          disabled={isUpdating}
        />
      </CommandSelectTrigger>
      <CommandSelectContent searchPlaceholder="Search ad objectives..." maxHeight="200px">
        <AdCampaignObjectivesContent
          adCampaignObjectives={adCampaignObjectives}
          isLoading={isLoading}
          selectedIds={selectedIds}
          isUpdating={isUpdating}
          handleSelectObjective={handleSelectObjective}
          updateSelection={updateSelection}
        />
      </CommandSelectContent>
    </CommandSelect>
  );
};
